// @flow
import React from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import type { Styles } from 'shared/types';
import { S, DARK } from '~/app/ui/tokens';

type OptionsProps = {
  maxLength: number,
  validation?: {
    pattern: RegExp;
    message: string;
  }
}

type Props = {
  type?: string,
  placeholder?: string,
  value: string,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  disabled?: boolean,
  additionalStyles: Object,
  readOnly?: boolean,
  options?: OptionsProps,
  /** For narrow columns (the page editor's inspector): full width, no empty info row. */
  compact?: boolean,
  /** The field's name for screen readers and AI assistants, when no <label> points at it. */
  ariaLabel?: string,
  id?: string,
};

export default function TextInput(props: Props) {
  const {
    type,
    placeholder,
    value = '',
    onChange,
    disabled,
    additionalStyles,
    readOnly = false,
    options,
    compact = false,
    ariaLabel,
    id
  } = props;

  const c: Styles<typeof styles> = useStyles(props);

  const characterLimit = options?.maxLength;
  const characterCount = value?.length;

  return (
    <>
      <input
        type={type}
        id={id}
        aria-label={ariaLabel}
        className={cx(c.textInput, compact && c.textInputCompact)}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        style={additionalStyles}
        readOnly={readOnly}
        maxLength={characterLimit}
        pattern={options?.validation?.pattern}
      />
      {(!compact || options?.validation || characterLimit >= 0) && <div className={cx(c.fieldInfo, compact && c.fieldInfoCompact)}>
        <label className={c.validationMessage}>{options?.validation?.message}</label>
        {characterLimit >= 0 && (
          <p className={cx(
            c.characterLimitMessage,
            characterCount >= characterLimit ? c.errorColor : c.defaultColor
          )}>
            {characterCount}/{characterLimit}
          </p>
        )}
      </div>}
    </>
  );
}

const styles = {
  characterLimitMessage: {
    flexBasis: '40px',
    fontSize: '12px',
    marginLeft: '24px',
    paddingLeft: '3px',
    marginTop: '4px',
    textAlign: 'right',
  },
  defaultColor: {
    color: 'gray',
    [DARK]: { color: S.muted }
  },
  errorColor: {
    color: '#993333',
    [DARK]: { color: S.red }
  },
  validationMessage: {
    color: '#993333',
    fontSize: '12px',
    visibility: 'hidden',
    [DARK]: { color: S.red }
  },
  textInput: {
    width: '90%',
    border: '1px solid hsl(0, 0%, 90%)',
    padding: '10px 10px',
    borderRadius: '3px',
    fontSize: '13px',
    marginLeft: '8px',
    color: 'hsl(0, 0%, 40%)',
    fontWeight: '400',
    outline: 'none',
    '&:disabled': {
      cursor: 'not-allowed',
      backgroundColor: 'hsl(0, 0%, 90%)',
      color: 'hsl(0, 0%, 73%)'
    },
    '&:invalid': {
      color: 'red',
      '& + div > label': {
        visibility: 'visible',
      }
    },
    [DARK]: {
      background: S.field,
      borderColor: S.fieldLine,
      color: S.fg,
      '&:disabled': { background: S.chrome, color: S.muted },
      '&:invalid': { color: S.red }
    }
  },
  textInputCompact: {
    width: '100%',
    boxSizing: 'border-box',
    marginLeft: 0,
    padding: '7px 8px',
    borderRadius: '6px',
    borderColor: S.fieldLine,
    color: S.fg,
    '&:focus': { borderColor: S.fg }
  },
  fieldInfoCompact: { marginLeft: 0, padding: '4px 0 0' },
  fieldInfo: {
    display: 'flex',
    marginLeft: '8px',
    padding: '10px 10px',
    justifyContent: 'space-between',
  }
};

const useStyles = createUseStyles(styles);
